import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import ImageDialog from 'common/components/ImageDialog';
import MuiDialog from 'common/components/MuiDialog';
import { coverPhoto, sortSampleMealPhotos } from 'modules/map/components/daily-dish/sample-meal/photoSort';
import type { SampleMealPhoto } from 'modules/map/components/daily-dish/sample-meal/types';

interface Props {
  open: boolean;
  onClose: () => void;
  photos: SampleMealPhoto[];
  title?: string;
}

const SampleMealGallery = (props: Props) => {
  const { open, onClose, photos, title } = props;
  const sorted = useMemo(() => sortSampleMealPhotos(photos).filter((p) => !!p.url), [photos]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) setIndex(0);
  }, [open, photos]);

  const current = sorted[index];
  const hasPrev = index > 0;
  const hasNext = index < sorted.length - 1;

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="desktop"
      fullWidth
      disablePortal
      titleAlign="center"
      disableCloseBtn
      PaperProps={{
        sx: {
          margin: 2,
          height: '100%',
          maxWidth: 'lg',
        },
      }}
    >
      <Box sx={{ position: 'relative', p: 2, height: '100%', overflow: 'auto' }}>
        <IconButton
          onClick={onClose}
          size="small"
          color="primary"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close fontSize="small" />
        </IconButton>

        {title && (
          <Typography variant="h6" textAlign="center" mb={2} color="primary.main">
            {title}
          </Typography>
        )}

        {sorted.length === 0 ? (
          <Typography textAlign="center" color="text.secondary">
            <FormattedMessage id="map.sampleMealPhotosEmptyDay" />
          </Typography>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: { xs: 220, tablet: 320 },
                aspectRatio: '1 / 1',
                '& > .MuiAvatar-root': {
                  width: '100% !important',
                  height: '100% !important',
                },
                '& img': { width: '100%', height: '100%', objectFit: 'cover' },
              }}
            >
              {current?.url ? (
                <ImageDialog src={current.url} alt={`sample-meal-${current.id ?? index}`} />
              ) : (
                <Typography color="error" variant="body2">
                  <FormattedMessage id="map.sampleMealPhotosLoadError" />
                </Typography>
              )}
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                onClick={() => setIndex((i) => i - 1)}
                disabled={!hasPrev}
                aria-label="previous"
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2">
                {index + 1} / {sorted.length}
              </Typography>
              <IconButton
                onClick={() => setIndex((i) => i + 1)}
                disabled={!hasNext}
                aria-label="next"
              >
                <ChevronRight />
              </IconButton>
            </Stack>

            {sorted.length > 1 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                {sorted.map((p, i) => {
                  const cover = coverPhoto([p]);
                  if (!cover?.url) return null;
                  return (
                    <Box
                      key={p.id ?? i}
                      component="img"
                      src={cover.url}
                      alt=""
                      onClick={() => setIndex(i)}
                      sx={{
                        width: 48,
                        height: 48,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        outline: i === index ? '2px solid' : 'none',
                        outlineColor: 'primary.main',
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </MuiDialog>
  );
};

export default SampleMealGallery;
